<script lang="ts" setup>
import { computed, ref, watch, onMounted, PropType } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BPagination,
  BSpinner,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useQuery } from '@vue/apollo-composable';
import { scrollToElement } from '@/utils/view-utils';
import { bucketUserRole } from '@/utils/bucket-utils';
import { deepParseDates, formatDate } from '@/utils/date-utils';
import {
  BucketsDocument,
  BucketsLiteDocument,
  BucketType,
} from '@/gql/graphql';
import {
  BUCKET_DATA_TYPES,
  PAGING_ITEMS_PER_PAGE,
  TRUNCATE_TEXT_LENGTH,
} from '@/constants';
import { useUserStore } from '@/store/user';
import { openRouterLink } from '@/utils/router-utils';
import { truncateText } from '@/utils/string-utils';

// Get the current route and router.
const route = useRoute();
const router = useRouter();

const userStore = useUserStore();

const activeBucketId = ref<number | null>(null);
const activeBucketPathPrefix = ref('');

// --- Component Properties ---
const props = defineProps({
  // component properties
  parentName: { type: String as PropType<string> },
  // bucket list filter
  dataType: String as PropType<string>,
  acrossUsers: { type: Boolean, default: false },
  bucketFriendlyNamePattern: {
    type: String as PropType<string | null>,
    default: null,
  },
  projectId: { type: Number as PropType<number | null>, default: null },
  // date & time filters
  createdAfter: { type: String as PropType<string | null>, default: null },
  createdBefore: { type: String as PropType<string | null>, default: null },
  // paging specification
  pagingItemsPerPage: { type: Number, default: PAGING_ITEMS_PER_PAGE },
  pagingHideControls: { type: Boolean, default: false },
  // additinal component props
  transparentStyle: { type: Boolean, default: false },
  messageNoItemsFound: {
    type: String,
    default: 'There are no data in your collection.',
  },
  showUserRole: {
    type: Boolean,
    default: true,
  },
  showBucketDescription: {
    type: Boolean,
    default: true,
  },
  queryDocument: {
    type: Object as PropType<
      typeof BucketsDocument | typeof BucketsLiteDocument
    >,
    default: BucketsDocument,
  },
});

// --- Component State ---
const pagingPageCurrent = ref<number>(1);
const pagingTotalRows = computed(() => {
  return bucketsQueryResult.value?.buckets?.total || 0;
});

// When the currentPathPrefix changes, update the URL's 'path' parameter.
watch(activeBucketPathPrefix, (newPath) => {
  // Update only the 'path' parameter, preserving the rest.
  router.replace({
    query: { ...route.query, path: newPath },
  });

  // Scroll to expanded accordion item with slight delay to allow for rendering.
  scrollToElement(`bucket-title-${activeBucketId.value}`, 500);
});

// When the activeAccordion changes (i.e. user selects a different bucket),
// update the URL's 'bucket' parameter and reset the currentPathPrefix.
watch(activeBucketId, (newBucketId, oldBucketId) => {
  // Build new query parameters, always updating the bucket param.
  const newQuery = {
    ...route.query,
    path: activeBucketPathPrefix.value,
    bucket: newBucketId ? newBucketId.toString() : undefined,
  };

  // Only clear the 'path' if there was a previous bucket and it changes.
  if (oldBucketId !== null && newBucketId !== oldBucketId) {
    newQuery.path = '';
    activeBucketPathPrefix.value = '';
  }
  router.replace({ query: newQuery });

  // Scroll to expanded accordion item with slight delay to allow for rendering.
  scrollToElement(`bucket-title-${newBucketId}`, 500);
});

// When the component mounts, initialize these values from the URL query parameters.
onMounted(() => {
  // If the query parameter 'bucket' is present, use it as the active bucket id.
  if (route.query.bucket) {
    activeBucketId.value = Number(route.query.bucket) || null;
  }
  // If the query parameter 'path' is present, set it as the current folder path.
  if (route.query.path) {
    activeBucketPathPrefix.value = String(route.query.path);
  }
});

// --- Query Buckets ---
const {
  result: bucketsQueryResult,
  loading: bucketsQueryInProgress,
  error: bucketsQueryError,
  refetch: refetchBuckets,
} = useQuery(props.queryDocument, {
  acrossUsers: props.acrossUsers,
  dataType: props.dataType,
  friendlyNamePattern: props.bucketFriendlyNamePattern,
  projectId: props.projectId,
  createdAfter: props.createdAfter,
  createdBefore: props.createdBefore,
  pagingPageCurrent: pagingPageCurrent.value,
  pagingItemsPerPage: props.pagingItemsPerPage,
});

// --- Buckets ---
const buckets = computed<BucketType[]>(() => {
  const rawList = bucketsQueryResult.value?.buckets?.buckets || [];
  const filtered = rawList.filter((bucket) => bucket != null);
  return filtered.map((bucket) => deepParseDates(bucket)) as BucketType[];
});

function requestRefetchBuckets() {
  refetchBuckets({
    acrossUsers: props.acrossUsers,
    dataType: props.dataType,
    createdAfter: props.createdAfter,
    createdBefore: props.createdBefore,
    friendlyNamePattern: props.bucketFriendlyNamePattern,
    projectId: props.projectId,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: props.pagingItemsPerPage,
  })
    ?.catch((err) => {
      console.error('Refetch buckets failed:', err);
    })
    ?.then(() => {
      console.debug('Refetched buckets');
    });
}

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  requestRefetchBuckets();
});

function requestSearch() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1; // this will trigger the watcher -> requestRefetchTasks
  } else {
    requestRefetchBuckets();
  }
}

// --- Expose Properties to the Outside ---
defineExpose({
  buckets,
  bucketsQueryInProgress,
  bucketsQueryError,
  requestSearch,
});

// --- Buckets Fields ---
const fields = computed(() => {
  let cols = [
    {
      key: 'createdAt',
      label: 'Created',
      formatter: formatDate,
      tdClass: 'align-middle',
    },
    {
      key: 'role',
      label: 'Role',
      tdClass: 'align-middle',
    },
    { key: 'friendlyName', label: 'Name', tdClass: 'align-middle' },
    { key: 'description', label: 'Description', tdClass: 'align-middle' },
    { key: 'actions', label: '', tdClass: 'align-middle text-end min-width' },
  ];

  if (!props.showUserRole) {
    cols = cols.filter((c) => c.key !== 'role');
  }

  if (!props.showBucketDescription) {
    cols = cols.filter((c) => c.key !== 'description');
  }

  return cols;
});

function handleDataRowClick(
  item: BucketType,
  index?: number,
  evt?: MouseEvent
) {
  if (item && item.id) {
    openRouterLink(
      {
        name: 'BucketDetail',
        params: { id: item.id },
        query: {
          showMetadata: '1',
          allowDownload: '1',
          listComputeTasks:
            props.dataType === BUCKET_DATA_TYPES.INPUT ? '1' : '0',
          listSamples: props.dataType === BUCKET_DATA_TYPES.INPUT ? '1' : '0',
          listProjects: props.dataType === BUCKET_DATA_TYPES.INPUT ? '1' : '0',
          listUsers: props.dataType === BUCKET_DATA_TYPES.INPUT ? '1' : '0',
          returnTo: encodeURIComponent(router.currentRoute.value.fullPath),
          returnLabel: props.parentName,
        },
      },
      router,
      evt
    );
  }
}
</script>

<template>
  <div>
    <!-- Error Fetching User Details -->
    <BAlert v-model="bucketsQueryError" variant="danger">
      {{ bucketsQueryError }}
      <b-button
        @click="requestRefetchBuckets"
        variant="outline-dark"
        class="btn-sm"
      >
        <font-awesome-icon icon="sync" />
        Retry
      </b-button>
    </BAlert>

    <!-- Show Buckets List -->
    <!-- Paging (above) -->
    <BPagination
      v-if="
        !props.pagingHideControls &&
        pagingItemsPerPage > 0 &&
        pagingTotalRows > props.pagingItemsPerPage
      "
      v-model="pagingPageCurrent"
      :total-rows="pagingTotalRows"
      :per-page="pagingItemsPerPage"
      pills
      size="sm"
      class="mb-2"
    />

    <BCard
      v-if="!bucketsQueryError"
      :class="{ 'transparent-card': props.transparentStyle }"
    >
      <b-table
        :items="buckets"
        :fields="fields"
        :busy="bucketsQueryInProgress"
        @row-clicked="handleDataRowClick"
        :striped="!props.transparentStyle"
        thead-tr-class="transparent-table"
        tbody-tr-class="transparent-table"
        :class="[
          'browser-table',
          'm-0',
          {
            'table-transparent': props.transparentStyle,
          },
        ]"
        show-empty
        small
        responsive
        hover
      >
        <!-- Table busy -->
        <template #table-busy>
          <div
            class="d-flex align-items-center justify-content-center"
            style="min-height: 200px"
          >
            <b-spinner small class="align-middle me-1"></b-spinner>
            <span class="ms-1">Loading data...</span>
          </div>
        </template>

        <!-- table empty -->
        <template #empty>
          <div class="text-center my-5">
            <p class="text-muted">No data found.</p>
          </div>
        </template>

        <!-- Table Content -->
        <!-- Role -->
        <template #cell(role)="data">
          {{ bucketUserRole(userStore.id, data.item) }}
        </template>
        <!-- Bucket description -->
        <template #cell(description)="data">
          <span :id="'bucket-description-' + data.item.id">
            {{ truncateText(data.value, TRUNCATE_TEXT_LENGTH) }}
          </span>
          <b-tooltip
            v-if="data.value && data.value.length > TRUNCATE_TEXT_LENGTH"
            :target="'bucket-description-' + data.item.id"
            class="large-tooltip"
          >
            {{ data.value }}
          </b-tooltip>
        </template>
        <!-- Actions -->
        <template #cell(actions)="data">
          <BButtonGroup>
            <BButton
              variant="outline-secondary"
              class="btn-sm"
              @click="handleDataRowClick(data.item, data.item.id, $event)"
              @click.stop
            >
              <font-awesome-icon :icon="['fas', 'database']" />
            </BButton>
          </BButtonGroup>
        </template>
      </b-table>
    </BCard>

    <!-- Paging (below) -->
    <BPagination
      v-if="
        !props.pagingHideControls &&
        pagingItemsPerPage > 0 &&
        pagingTotalRows > props.pagingItemsPerPage
      "
      v-model="pagingPageCurrent"
      :total-rows="pagingTotalRows"
      :per-page="pagingItemsPerPage"
      pills
      size="sm"
      class="mt-2 mb-0"
    />
  </div>
</template>
